import React from 'react'

import { Box, SearchField, Text } from '@factorialco/deprecated-design-system'
import { PerformanceCycleFragment } from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import RemoveValue from 'modules/core/components/Filter/base/ClearValue'
import SingleChoice from 'modules/core/components/Filter/filters/SingleChoice'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

export const NO_CYCLE_OPTION = 'NO_CYCLE'

export const CycleFilter: React.FC<{
  value: string | null
  noCycleSelected?: boolean
  cycles: PerformanceCycleFragment[]
  onChange: (value: string | null) => void
}> = ({ value, noCycleSelected, cycles, onChange }) => {
  const { search, debouncedSearch, onSearch, clearSearch } = useDebouncedSearch()

  const options = useUnsafeMemo(() => {
    const list = sortBy(
      cycles.map((cycle) => ({ label: cycle.title, value: cycle.id })),
      ['label']
    )

    if (list.length > 0) {
      list.unshift({
        label: i18n.t('performance_cycles.cycle_filter.no_cycle_option'),
        value: NO_CYCLE_OPTION,
      })
    }

    return list
  }, [cycles])

  const selectedOption = options.find((option) =>
    noCycleSelected ? option.value === NO_CYCLE_OPTION : option.value === value
  )

  const optionsToShow = useUnsafeMemo(() => {
    if (!debouncedSearch) return options

    const list = options.filter((option) => matchText(debouncedSearch, option.label))
    if (selectedOption && !list.includes(selectedOption)) {
      list.unshift(selectedOption)
    }

    return list
  }, [options, selectedOption, debouncedSearch])

  return (
    <Box flexDirection="row">
      <SingleChoice
        header={
          <Box padding="s8">
            <SearchField search={search} onSearch={onSearch} variant="big" placeholder="" />
          </Box>
        }
        label={i18n.t('performance_cycles.cycle_filter.label')}
        value={noCycleSelected ? NO_CYCLE_OPTION : value}
        onValueChange={onChange}
        options={optionsToShow}
        onClose={clearSearch}
        footer={
          options.length === 0 ? (
            <Box justifyContent="center" alignItems="center" padding="s16">
              <Text.Normal size="300" weight="normal" color="textSecondary">
                {i18n.t('performance_cycles.cycle_filter.blank')}
              </Text.Normal>
            </Box>
          ) : (
            <RemoveValue />
          )
        }
      />
    </Box>
  )
}
